import React, { useMemo } from 'react';
import {
  DESCUENTOS_REPORT_TITULO_DEFAULT,
  applyDescuentosColumnLabelsFromApi,
  formatDescuentosCell,
  formatDescuentosTotalCell,
  mapApiDescuentosTotalesToNormIds,
  mergeDescuentosEncabezado,
  mergeDescuentosFootTotals,
  resolveDescuentosColumns,
  rowToDescuentosNorm,
  sumDescuentosTotals,
  withDescuentosPeriodoLine,
} from '../../../utils/descuentosCortesiasReportModel';
import { encabezadoTieneDatos } from '../../../utils/reporteEncabezadoApi';
import ReporteFiltrosEncabezadoLista from '../ReporteFiltrosEncabezadoLista/ReporteFiltrosEncabezadoLista';
import '../PopularidadReportLayout/PopularidadReportLayout.css';
import './DescuentosCortesiasReportLayout.css';

const meaningfulHeaderLine = (v) => v != null && String(v).trim() !== '';

export default function DescuentosCortesiasReportLayout({
  className = '',
  rawRows = [],
  fechaInicio,
  fechaFin,
  cajaInicio,
  cajaFin,
  turnoInicio,
  turnoFin,
  encabezadoApi = null,
  encabezadoFallback = null,
  reporteTitulo = DESCUENTOS_REPORT_TITULO_DEFAULT,
  apiTotales = null,
  columnasLabels = null,
  sucursalNombre = '',
}) {
  const enc = useMemo(() => {
    const m = mergeDescuentosEncabezado(encabezadoApi, encabezadoFallback);
    return withDescuentosPeriodoLine({ ...m, reporteTitulo: m.reporteTitulo || reporteTitulo }, fechaInicio, fechaFin);
  }, [encabezadoApi, encabezadoFallback, reporteTitulo, fechaInicio, fechaFin]);

  const resolved = useMemo(() => {
    const base = resolveDescuentosColumns(rawRows[0] ?? null);
    return applyDescuentosColumnLabelsFromApi(base, columnasLabels);
  }, [rawRows, columnasLabels]);

  const { normRows, totals } = useMemo(() => {
    const n = rawRows.map((r) => rowToDescuentosNorm(r, resolved));
    const summed = sumDescuentosTotals(n, resolved);
    return {
      normRows: n,
      totals: mergeDescuentosFootTotals(summed, mapApiDescuentosTotalesToNormIds(apiTotales), resolved),
    };
  }, [rawRows, resolved, apiTotales]);

  const tituloMostrar = String(enc.reporteTitulo || reporteTitulo).toUpperCase();

  return (
    <div className={`dc-report pop-report ${className}`.trim()}>
      <header className="pop-report-headerenc">
        {encabezadoTieneDatos(enc) &&
        enc.bloqueTexto &&
        !enc.nombreComercial &&
        !enc.reporteTitulo ? (
          <pre className="pop-report-headerenc__bloque">{enc.bloqueTexto}</pre>
        ) : (
          <>
            {(meaningfulHeaderLine(enc.nombreComercial) ||
              meaningfulHeaderLine(enc.rfc)) && (
              <div className="pop-report-headerenc__empresa">
                {meaningfulHeaderLine(enc.nombreComercial) && (
                  <h1 className="pop-report-headerenc__nombre">
                    {String(enc.nombreComercial).trim().toUpperCase()}
                    {sucursalNombre && !String(enc.nombreComercial).toUpperCase().includes(sucursalNombre.toUpperCase()) && (
                      <span className="pop-report-headerenc__sucursal-append"> - {sucursalNombre.toUpperCase()}</span>
                    )}
                  </h1>
                )}
                {meaningfulHeaderLine(enc.rfc) && (
                  <p className="pop-report-headerenc__detalle pop-report-headerenc__rfc">
                    RFC: {String(enc.rfc).trim().toUpperCase()}
                  </p>
                )}
              </div>
            )}
            <div className="pop-report-headerenc__titulos">
              <h2 className="pop-report-headerenc__reporte dc-report-title-highlight">{tituloMostrar}</h2>
            </div>
            <div className="pop-report-headerenc__filtros">
              <p className="pop-report-headerenc__filtros-line">{enc.periodoTexto}</p>
              {enc.filtrosTexto && (
                <p className="pop-report-headerenc__filtros-line pop-report-headerenc__filtros-line--extra">
                  {enc.filtrosTexto}
                </p>
              )}
              <ReporteFiltrosEncabezadoLista
                className="reporte-filtros-meta--report"
                label="Sucursales"
                texto={enc.sucursalesTexto || sucursalNombre}
                items={enc.sucursalesItems}
              />
              <ReporteFiltrosEncabezadoLista
                className="reporte-filtros-meta--report"
                label="Cajas"
                texto={
                  enc.cajasTexto ||
                  (cajaInicio != null || cajaFin != null
                    ? `${cajaInicio ?? '—'} a ${cajaFin ?? '—'}`
                    : '')
                }
                items={enc.cajasItems}
              />
              <p className="pop-report-headerenc__filtros-line">
                {enc.turnosTexto ? (
                  <>Turnos: {String(enc.turnosTexto)}</>
                ) : (
                  <>
                    Turnos: <strong>{turnoInicio ?? '—'}</strong> a <strong>{turnoFin ?? '—'}</strong>
                  </>
                )}
              </p>
            </div>
          </>
        )}
      </header>

      <div className="dc-report-table-wrap">
        <table className="dc-report-table">
          <thead>
            <tr>
              {resolved.map((c) => (
                <th key={c.id} className={c.align === 'right' ? 'dc-num' : 'dc-text'}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {normRows.length === 0 ? (
              <tr>
                <td className="dc-report-empty-row" colSpan={resolved.length}>
                  Sin movimientos en el rango seleccionado.
                </td>
              </tr>
            ) : (
              normRows.map((r, idx) => (
                <tr key={idx}>
                  {resolved.map((c) => (
                    <td key={`${idx}-${c.id}`} className={c.align === 'right' ? 'dc-num' : 'dc-text'}>
                      {formatDescuentosCell(c, r[c.id])}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
          <tfoot>
            <tr className="dc-report-totals">
              {resolved.map((c) => (
                <td
                  key={c.id}
                  className={
                    c.total
                      ? `dc-num dc-report-totals-num`
                      : `dc-text dc-report-totals-spacer`
                  }
                >
                  {c.total ? formatDescuentosTotalCell(c, totals[c.id]) : ''}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
